import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTodayItems, calendarItemDay } from '../../../hooks/useTodayItems';
import { UnifiedCalendarItem, CalendarItemType } from '../../../types/calendar';

interface TimelineItem {
  id: string;
  time: string;          // "09:00" or "all day"
  label: string;         // title
  kind: 'event' | 'invoice' | 'bill' | 'job';
  day: string;           // 'YYYY-MM-DD' — where a click should land
  isToday: boolean;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * The rail's four visual buckets. The calendar has seven item types; recurring
 * templates are money like their concrete documents, so they collapse onto the
 * same class rather than inventing two more colours in a narrow rail.
 */
const KIND_BY_TYPE: Record<CalendarItemType, TimelineItem['kind']> = {
  event: 'event',
  invoice_due: 'invoice',
  invoice_created: 'invoice',
  recurring_invoice: 'invoice',
  recurring_expense: 'bill',
  bill_due: 'bill',
  job_due: 'job',
};

function toTimelineItem(item: UnifiedCalendarItem, todayDay: string): TimelineItem {
  const day = calendarItemDay(item);
  const isToday = day === todayDay;
  const timed = !item.allDay && item.start.includes('T');
  const d = timed ? new Date(item.start) : null;

  let time: string;
  if (!isToday) {
    // A later item's useful fact is WHICH day, not what o'clock.
    const parsed = new Date(`${day}T12:00:00`);
    time = isNaN(parsed.getTime())
      ? day
      : parsed.toLocaleDateString(undefined, { weekday: 'short' }).toLowerCase();
  } else if (d && !isNaN(d.getTime())) {
    time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  } else {
    time = 'all day';
  }

  return {
    id: item.id,
    time,
    label: item.title || '(untitled)',
    kind: KIND_BY_TYPE[item.type] ?? 'event',
    day,
    isToday,
  };
}

/**
 * TodayTimeline — sticky right rail listing today's calendar items, then the
 * next 4 days as a lighter "later" group.
 *
 * Built on `useTodayItems`, which composes the SAME item builders the calendar
 * renders from. This rail used to re-derive all of it independently, which is
 * how it and the calendar came to disagree about what was on today — it was one
 * of five separate "what's on my plate" composers in this app.
 */
const TodayTimeline: React.FC = () => {
  const navigate = useNavigate();
  const { today, later, todayDay } = useTodayItems({ days: 4 });

  const todayItems = useMemo(
    () => today.map(i => toTimelineItem(i, todayDay)),
    [today, todayDay]
  );
  const laterItems = useMemo(
    () => later.map(i => toTimelineItem(i, todayDay)),
    [later, todayDay]
  );

  // Every row used to be a dead div. The calendar is routable now, so a row can
  // open the day it belongs to.
  const openDay = (day: string) => navigate(`/calendar?date=${day}`);

  const renderRow = (item: TimelineItem) => (
    <li
      key={item.id}
      className={`home-today__item home-today__item--${item.kind}`}
      onClick={() => openDay(item.day)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDay(item.day);
        }
      }}
      role="button"
      tabIndex={0}
      title={`Open ${item.day} on the calendar`}
      style={{ cursor: 'pointer' }}
    >
      <span className="home-today__time">{item.time}</span>
      <span className="home-today__kind">{item.kind}</span>
      <span className="home-today__text">{item.label}</span>
    </li>
  );

  return (
    <div className="home-today">
      <div className="home-today__header">
        <span className="home-today__lead">──</span>
        <span className="home-today__label">TODAY</span>
        <span className="home-today__fill" aria-hidden="true" />
      </div>

      {todayItems.length === 0 ? (
        <div className="home-today__empty">· nothing scheduled ·</div>
      ) : (
        <ul className="home-today__list">{todayItems.map(renderRow)}</ul>
      )}

      {laterItems.length > 0 && (
        <>
          <div className="home-today__header home-today__header--dim">
            <span className="home-today__lead">──</span>
            <span className="home-today__label">LATER</span>
            <span className="home-today__fill" aria-hidden="true" />
          </div>
          <ul className="home-today__list home-today__list--dim">
            {laterItems.slice(0, 8).map(renderRow)}
          </ul>
        </>
      )}

      <button
        type="button"
        className="home-today__all"
        onClick={() => navigate('/calendar')}
        style={{
          display: 'block',
          marginTop: 10,
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'var(--bs-secondary-color)',
          cursor: 'pointer',
        }}
      >
        full calendar →
      </button>
    </div>
  );
};

export default TodayTimeline;
